import classNames from 'classnames';

import XMarkIcon from '@heroicons/react/20/solid/XMarkIcon';
import { BUTTON_ANIMATION, getButtonColorClasses, getButtonSizeClasses, removeHoverClasses } from './constants';
import { m } from 'framer-motion';

import { ButtonProps } from './Button';
import { useCallback } from 'react';
import { Tooltip } from '../tooltip/Tooltip';
import { getCornerStyleClassname, Size } from '../../../constants';
import { useTheme } from '../../../context/theme/useTheme';
import { ThemePrimary } from '../../../context/theme/ThemeConstantsUpdated';

const { primaryShadow } = ThemePrimary;

export const IconButton = (props: IconButtonProps) => {
  const { onClick, className, buttonType, iconSizeOverride, options = {}, disabled, iconColorOverride } = props;
  const { includeShadow = false, size = Size.md, tooltip, includeAnimation, cornerStyle = Size.xl } = options;
  const { isDarkMode } = useTheme();
  const Icon = props.Icon || XMarkIcon;
  const colorClasses = getButtonColorClasses({
    buttonType,
    isDarkMode,
  });
  const { iconSizeClasses, iconButtonSizeClasses } = getButtonSizeClasses(size);

  const Btn = useCallback(
    () => (
      <m.button
        type="button"
        onClick={onClick}
        disabled={disabled}
        {...(includeAnimation ? BUTTON_ANIMATION : {})}
        className={classNames(
          className,
          colorClasses,
          iconButtonSizeClasses,
          includeShadow && primaryShadow,
          getCornerStyleClassname(cornerStyle),
          'relative focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
          disabled ? ['cursor-default opacity-50', removeHoverClasses(colorClasses)] : colorClasses
        )}
      >
        <Icon
          className={classNames(
            'abs-middle',
            iconSizeOverride != null ? iconSizeOverride : iconSizeClasses,
            iconColorOverride
          )}
          aria-hidden="true"
        />
      </m.button>
    ),
    [
      Icon,
      className,
      colorClasses,
      iconButtonSizeClasses,
      iconSizeClasses,
      iconSizeOverride,
      includeAnimation,
      includeShadow,
      cornerStyle,
      onClick,
      primaryShadow,
      disabled,
    ]
  );

  return tooltip != null ? (
    <Tooltip tip={tooltip}>
      <Btn />
    </Tooltip>
  ) : (
    <Btn />
  );
};

export type IconButtonProps = Omit<ButtonProps, 'children'> & {
  iconSizeOverride?: string;
};
